// Public catalog copy recovered from the lineage-bound .app bundle.
// Inventory SHA-256: f2d61b46b1cf5ecd141f53c34f9cd7f46f42d1361c5b6fb5a187ffed24fc1327.
// CAP IDs are source references, not tenant entitlement or runtime verification.
export const dbk_usServicesCopy = {
  heading: "Explore Our Accounting & AI Agent Services",
  intro: [
    "Discover how DynamicBooks helps you manage accounting workflows with specialized AI agents. From organizing financial records and reviewing transactions to preparing reports and identifying exceptions, explore the tools available to support your everyday accounting operations.",
    "You stay in control of your accounting decisions, while intelligent agents help reduce repetitive work and keep information organized."
  ],
  availability: "These catalog entries describe software workflows, not confirmation that they are enabled for your account. Customer availability requires confirmation. An estimate does not connect accounts or activate services.",
  importantHeading: "Important Information About Your Services",
  important: "DynamicBooks provides AI-powered self-service accounting workflows. You remain responsible for reviewing and approving accounting decisions. Available integrations and features depend on your account configuration and enabled services. Tax filing, payroll processing, and professional accounting services are not included in the current self-service offering.",
  regionalLimit: "Swedish VAT, SIE and statutory report tools are not U.S. accounting services.",
  integrationLimit: "QuickBooks API availability alone is not a delivered customer workflow. No automatic posting, invoice payments or broader synchronization is promised here.",
  estimateHref: "/us/intake",
  quickBooksHref: "/quickbooks",
  logoSrc: "/assets/brand/production/v1/svg/erg-dbk-logo-horizontal-on-dark.svg"
} as const;

export const dbk_usServiceCategories = [
  { id: "erg-dbk-services-records", title: "Records & Workspace" },
  { id: "erg-dbk-services-bookkeeping", title: "Bookkeeping & Transactions" },
  { id: "erg-dbk-services-setup", title: "Accounting Setup" },
  { id: "erg-dbk-services-reports", title: "Reports & Financial Visibility" },
  { id: "erg-dbk-services-agents", title: "AI Agents & Review" }
] as const;

type Workflow = Readonly<{
  id: string;
  category: typeof dbk_usServiceCategories[number]["title"];
  title: string;
  body: string;
  benefit: string;
  review: string;
  access: string;
  evidenceId: string;
}>;

// Preserve all original source fields and caveats. No enabled/availability flags
// exist in the observed public data; do not invent or infer them from CAP IDs.
export const dbk_usServiceWorkflows = [
  {
    "access": "Authorized DynamicMail Data Room",
    "benefit": "Keep accounting evidence together and find the records behind your work.",
    "body": "Organize invoices, receipts and supporting files in your DynamicMail Data Room.",
    "category": "Records & Workspace",
    "evidenceId": "CAP-001",
    "id": "financial-records",
    "review": "You select the documents and control sharing. Uploading a file does not post an accounting entry.",
    "title": "Financial document organization"
  },
  {
    "access": "Accounting journal document controls",
    "benefit": "Make entries easier to investigate and review.",
    "body": "Open the supporting documents for a journal entry and manage links between records and accounting work.",
    "category": "Records & Workspace",
    "evidenceId": "CAP-002",
    "id": "document-links",
    "review": "Check that a document belongs to the correct entry before linking it; linking does not change the journal amount.",
    "title": "Journal supporting-document links"
  },
  {
    "access": "Accounts Payable workspace",
    "benefit": "Reduce repetitive preparation and spot missing or unmatched records.",
    "body": "Review inbound bills and expense records, suggested accounting treatment, open items and document links.",
    "category": "Bookkeeping & Transactions",
    "evidenceId": "CAP-003",
    "id": "payables",
    "review": "You check suggestions before authorized posting. Recording a payment status does not send money.",
    "title": "Bills and expense review"
  },
  {
    "access": "Accounts Receivable workspace",
    "benefit": "Understand outstanding customer amounts and investigate unmatched items.",
    "body": "Review outbound invoice records, receivable balances and their accounting and payment-status evidence.",
    "category": "Bookkeeping & Transactions",
    "evidenceId": "CAP-004",
    "id": "receivables",
    "review": "You review the accounting treatment. This is not a promise of invoice payment collection or automatic QuickBooks synchronization.",
    "title": "Customer invoice and receivable review"
  },
  {
    "access": "Bank Transactions workspace with import permission",
    "benefit": "Bring source transactions into one review queue.",
    "body": "Import a supported bank CSV, inspect transactions and prepare matching or categorization suggestions.",
    "category": "Bookkeeping & Transactions",
    "evidenceId": "CAP-005",
    "id": "bank-records",
    "review": "Check import mappings, duplicates and suggestions before authorized posting. Bank connections depend on the provider and your account's availability.",
    "title": "Bank transaction import and matching"
  },
  {
    "access": "Fixed Asset Ledger",
    "benefit": "Keep asset changes and their supporting evidence visible.",
    "body": "Review asset balances and supporting entries, link documents, and preview write-downs or register adjustments.",
    "category": "Bookkeeping & Transactions",
    "evidenceId": "CAP-007",
    "id": "fixed-assets",
    "review": "Review assumptions and preview effects before confirming. Tax depreciation advice or filing is not included.",
    "title": "Fixed-asset records and adjustments"
  },
  {
    "access": "Account Plan in an existing configured accounting tenant",
    "benefit": "Use a consistent structure for transaction preparation and reporting.",
    "body": "View and maintain your configured account plan and account classifications.",
    "category": "Accounting Setup",
    "evidenceId": "CAP-008",
    "id": "account-plan",
    "review": "The U.S. starter chart is source-gated setup scaffolding, not a certified U.S. GAAP or tax configuration. Obtain suitable accounting review before use.",
    "title": "Chart-of-accounts management"
  },
  {
    "access": "Financial report period and fiscal-year controls",
    "benefit": "Review financial activity for the intended date range.",
    "body": "Select reporting periods and maintain fiscal-year settings used by financial reports.",
    "category": "Accounting Setup",
    "evidenceId": "CAP-009",
    "id": "fiscal-years",
    "review": "You confirm the company's fiscal dates and accounting setup; no tax election or filing is made.",
    "title": "Fiscal-year and reporting-period setup"
  },
  {
    "access": "Balance Sheet report; export permission for downloads",
    "benefit": "See the financial position and inspect underlying account balances.",
    "body": "View assets, liabilities and equity from your accounting records, with period filters and report exports.",
    "category": "Reports & Financial Visibility",
    "evidenceId": "CAP-010",
    "id": "balance-sheet",
    "review": "Check completeness, classifications and adjustments. The report is not an audit or professional certification.",
    "title": "Balance-sheet preparation"
  },
  {
    "access": "Profit & Loss report; export permission for downloads",
    "benefit": "Understand recorded performance without assembling every line manually.",
    "body": "Review income and expenses for a selected period and export the prepared report.",
    "category": "Reports & Financial Visibility",
    "evidenceId": "CAP-011",
    "id": "profit-loss",
    "review": "You review source records, period boundaries and accounting treatment before relying on the report.",
    "title": "Profit-and-loss preparation"
  },
  {
    "access": "General Ledger report",
    "benefit": "Trace a balance back to the activity behind it.",
    "body": "Inspect account-level movements and transactions for a selected date range.",
    "category": "Reports & Financial Visibility",
    "evidenceId": "CAP-012",
    "id": "general-ledger",
    "review": "You investigate exceptions and approve any separate corrections; viewing a report does not post changes.",
    "title": "General-ledger review"
  },
  {
    "access": "Journal Entries report; export permission for downloads",
    "benefit": "Keep a readable record of accounting work and its evidence.",
    "body": "Browse recorded journal entries, inspect supporting documents and export the register.",
    "category": "Reports & Financial Visibility",
    "evidenceId": "CAP-013",
    "id": "journal-register",
    "review": "Review entries and links for accuracy. Exports do not submit a filing.",
    "title": "Journal-entry register"
  },
  {
    "access": "Account Balances report",
    "benefit": "Identify unexpected balances and focus your reconciliation work.",
    "body": "View opening balances, period movements and closing balances by account.",
    "category": "Reports & Financial Visibility",
    "evidenceId": "CAP-014",
    "id": "account-balances",
    "review": "You confirm the account mapping and investigate discrepancies.",
    "title": "Account-balance review"
  },
  {
    "access": "Bank Transactions report",
    "benefit": "Compare financial records with source activity during reconciliation.",
    "body": "Review recorded cash and bank movements alongside imported transaction history.",
    "category": "Reports & Financial Visibility",
    "evidenceId": "CAP-015",
    "id": "cash-history",
    "review": "You resolve missing, duplicate or unmatched transactions; no payment is initiated.",
    "title": "Bank and cash activity review"
  },
  {
    "access": "Reports Register and archive controls for supported reports",
    "benefit": "Keep report versions and their supporting materials together.",
    "body": "Review previously prepared report artifacts and build an archive of supported accounting records.",
    "category": "Reports & Financial Visibility",
    "evidenceId": "CAP-016",
    "id": "report-records",
    "review": "Check the selected content and manage archive keys securely. A local report or filing record is not evidence of external submission or compliance.",
    "title": "Report history and accounting archives"
  },
  {
    "access": "DynamicBooks Accounting Control Center",
    "benefit": "Focus on exceptions and follow up without losing context.",
    "body": "Review control-center issues, create accounting tasks, and track open, blocked and completed work.",
    "category": "AI Agents & Review",
    "evidenceId": "CAP-017",
    "id": "exception-tasks",
    "review": "You decide priorities and confirm whether work is complete; a task status is not accounting approval.",
    "title": "Accounting exception and task tracking"
  },
  {
    "access": "Executable agent task in an authorized DynamicMail room; Agent Credits and permissions apply",
    "benefit": "Reduce manual investigation while retaining a reviewable result.",
    "body": "Ask an authorized room-bound accounting agent to inspect records and prepare a dry-run result for a control-center task.",
    "category": "AI Agents & Review",
    "evidenceId": "CAP-018",
    "id": "agent-preparation",
    "review": "The task execution path is preview-only: no journal posting, payment, payroll approval or live filing. Any consequential action needs its separate authorized confirmation workflow.",
    "title": "Agent-assisted accounting task preparation"
  }
] as const satisfies readonly Workflow[];

// This is a copy-verification boundary, never a backend capability flag.
export const dbk_usServiceVerification = {
  customerAvailability: "not_verified",
  source: "erg-dbk-catalog-public-source-003"
} as const;

export function dbk_usWorkflowCaveat(id: string): string {
  if (id === "agent-preparation") return "Preview-only task preparation";
  if (id === "account-plan") return "Source-gated U.S. setup";
  if (id === "fixed-assets") return "Preview adjustments before confirmation";
  return "Configuration and permissions required";
}

export function dbk_searchUsServices(query: string): readonly Workflow[] {
  const needle = query.trim().toLocaleLowerCase("en-US");
  return dbk_usServiceWorkflows.filter(workflow =>
    [workflow.category, workflow.title, workflow.body, workflow.benefit, workflow.review, workflow.access]
      .join(" ").toLocaleLowerCase("en-US").includes(needle)
  );
}
